import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Actions
import { saveMapDetails } from "../../../actions/map-view/SaveMapStateActions";
import { getCampaignOverview } from "../../../actions/campaign-report/CampaignReportActions";

// Utils and Constants
import {
  bcForAnalyserMapView,
  bcForPlanningMapView,
} from "../../../utils/BreadCrumb";
import {
  CampaignMapViewId,
  LocalStorageKeys,
  OrganizationIdsMapping,
  UrlKeys,
} from "../../../constants/GeneralConstants";
import { useUrlKeyCheck } from "../../../mavin/utils/hooks/HooksUtil";
import { getItem } from "../../../utils/localstorage";
import { checkIsOrgSpecific } from "../../../utils/OrgUtils";
import { downloadDomToImage } from "../../../common-utils/image-utils/ImageUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import {
  ButtonWithLoader,
  OutlineButtonWithLoader,
} from "../../../mavin/components/button/Button";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import NavbarBranding from "../../../components/top-nav/NavbarBranding";
import { UserPricing } from "../../../components/top-nav/TopNav";

// MapView Loader
export function MapViewLoader() {
  return (
    <div className="position-absolute text-center w-100 mt-5">
      <Spinner className="mt-4" />
    </div>
  );
}

// BreadcrumbSection
export function BreadcrumbSection() {
  const { id, cityId } = useParams();

  // Selector State
  const restoreLoading = useSelector(
    (state) => state.saveMapState.restoreLoading
  );
  const cityName = useSelector((state) => state.saveMapState.cityName);

  // Conditionally rendered breadcrumb
  const analyserBreadcrumbItems = bcForAnalyserMapView(id, cityId, cityName);
  const planningBreadcrumbItems = bcForPlanningMapView(id, cityId, cityName);

  const { campaignPlanning } = UrlKeys;

  // checking "plan" key in url and render "Breadcrumb" conditionally
  // if the plan is present in url then its is from campaign planning page
  const isFromCampaignPlanner = useUrlKeyCheck(campaignPlanning);

  // loading condition
  if (restoreLoading) {
    return null;
  }

  // Return
  if (isFromCampaignPlanner) {
    return <Breadcrumb items={planningBreadcrumbItems} />;
  }

  return <Breadcrumb items={analyserBreadcrumbItems} />;
}

/**
 * Download Campaign Map Image
 */
export function DownloadCampaignMapImage() {
  const dispatch = useDispatch();
  const { id, cityId } = useParams();

  // State
  const [isImageDownloading, setIsImageDownloading] = useState(false);

  // city name
  const cityName = useSelector((state) => state.saveMapState.cityName);

  // campaign summary
  const campaignSummaryLoading = useSelector(
    (state) => state.campaignReport.campaignSummaryLoading
  );
  const campaignSummary = useSelector(
    (state) => state.campaignReport.campaignSummary
  );
  const { campaign = {} } = campaignSummary || {};
  const { title: campaignTitle = "" } = campaign || {};

  // campaign summary for campaignTitle
  useEffect(() => {
    dispatch(getCampaignOverview(id));
  }, [dispatch, id, cityId]);

  // filename
  const imageFileName = `${campaignTitle}-${cityName}-MapView`;

  // functions
  async function downloadCampaignMapViewImage() {
    const input = document.getElementById(CampaignMapViewId);
    setIsImageDownloading(true); // show loader

    try {
      await downloadDomToImage(input, imageFileName);

      // toaster
      toast.success("Map State Downloaded");
    } catch (err) {
      toast.error("Download failed");
      console.error("Download failed:", err);
    } finally {
      setIsImageDownloading(false); // hide loader
    }
  }

  return (
    <OutlineButtonWithLoader
      displayContent={"Download Map State"}
      onClickFunction={downloadCampaignMapViewImage}
      loader={isImageDownloading}
      isDisabled={isImageDownloading || campaignSummaryLoading}
    />
  );
}

// Campaign MapView Header
export function CampaignMapViewHeader() {
  const dispatch = useDispatch();
  const { id, cityId } = useParams();

  const { campaignPlanning } = UrlKeys;
  // checks map view is from campaign analyser or campaign planner
  // if it is from campaign analyser - saveMapAsImage = true
  const saveMapAsImage = !useUrlKeyCheck(campaignPlanning);

  // Selector State
  const saveMapViewLoading = useSelector((state) => state.saveMapState.loading);

  // patrika
  const user = JSON.parse(getItem(LocalStorageKeys.USER));
  const { orgId, userId } = user || {};
  const { patrika } = OrganizationIdsMapping;
  const isPatrikaSpecific = checkIsOrgSpecific(orgId, patrika);

  return (
    <nav className="main-header map-header navbar navbar-expand">
      <NavbarBranding />

      {/* Conditionally rendered breadcrumb */}
      <BreadcrumbSection />

      <div className="navbar-nav ml-auto">
        <div className="d-flex align-items-center">
          {/* Patrika Price Settings Button */}
          {isPatrikaSpecific && (
            <UserPricing
              userId={userId}
              isDropdownItem={false}
              className="mr-4"
            />
          )}

          {/* Download MapImage */}
          <DownloadCampaignMapImage />

          {/* Save Map State Button*/}
          <ButtonWithLoader
            displayContent={"Save Map State"}
            onClickFunction={() =>
              dispatch(saveMapDetails(id, cityId, saveMapAsImage))
            }
            loader={saveMapViewLoading}
            isDisabled={saveMapViewLoading}
            className="ml-3"
          />
        </div>
      </div>
    </nav>
  );
}
